// Messaging Reports (/analytics/messaging). Modeled on the Calls page
// (RhinoVOIPReports): same filter bar, tabs, Graph/Table toggle, CSV export
// and palette. One API call (GET /analytics/messages/breakdown) returns counts
// grouped finely enough that every filter and chart is derived in the browser.
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import {
  Button, DateRangeDropdown, Icon, UtilityInlineGrid,
} from 'rhinostyle';
import PageLoader from './PageLoader';
import AskReggieButton from './AskReggieButton';
import AnalyticsFilterDropdown from './AnalyticsFilterDropdown';
import {
  OverviewSummary, OverviewMembers, OverviewChannels, OverviewGroups, OverviewReminders,
} from './MessagingReportsOverview';
import {
  ChartCard, SortableTable, GroupingSelector, csvColumns, MAX_SERIES, OTHER_LABEL,
} from './MessagingReportsCharts';
import {
  useGetMessageBreakdownQuery, useGetInboundContactsQuery, useGetMessagesByGroupQuery, useGetRemindersSummaryQuery, useGetOpenByMemberQuery,
  useGetMessageOverageStatusQuery,
} from '../services/AnalyticsService';
import {
  REPORT_DATE_PICKER_LABELS, REPORT_TIMEZONE_OPTIONS, readStoredTimezone, storeTimezone,
} from '../helpers/ReportsHelpers';
import {
  MESSAGE_KINDS, channelTypeLabel, filterRows, sumRows, daysInRange, summaryByPeriod, kindPie,
  perDaySeries, perHourSeries, perWeekdaySeries, peakInboundHour, totalsByCategory, categoryByPeriod, dailyByCategory,
  formatMinutes, OTHER_KEY,
} from '../helpers/MessagingReportsHelpers';
import { clearAskReggieContext, setAskReggieContext } from '../reducers/rhinoanalystReducer';
import { capitalize } from '../helpers/StringHelpers';

const TZ_STORAGE_KEY = 'messagingReportsTimezone';
// Graph (cards and charts) or Table (each tab's data as a sortable table). Remembered per browser.
// The id stays 'overview' so a stored preference from before the rename still applies.
export const VIEW_STORAGE_KEY = 'messagingReportsView';
const REPORT_VIEWS = [
  { id: 'overview', label: 'Graph' },
  { id: 'table', label: 'Table' },
];
const readStoredView = () => {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    return REPORT_VIEWS.some((v) => v.id === stored) ? stored : 'overview';
  } catch {
    return 'overview';
  }
};
const MAX_RANGE_MONTHS = 12;

const REPORT_TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'members', label: 'Members' },
  { id: 'channels', label: 'Channels' },
  { id: 'groups', label: 'Groups' },
  { id: 'reminders', label: 'Appointment Reminders' },
];

const REMINDER_COLORS = {
  Confirmed: '#5DBE85',
  Cancelled: '#E94B4F',
  'No Response': '#B5B7BD',
};

const pieOf = (rows, nameKey = 'name', valueKey = 'total') => rows.filter((r) => r[valueKey] > 0).map((r) => ({ name: r[nameKey], value: r[valueKey] }));

const MessagingReports = () => {
  const dispatch = useDispatch();
  const [reportView, setReportView] = useState(readStoredView);
  const [activeTab, setActiveTab] = useState('summary');
  const [startDate, setStartDate] = useState(moment().subtract(29, 'days'));
  const [endDate, setEndDate] = useState(moment());
  const [activeKey, setActiveKey] = useState(3);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedKind, setSelectedKind] = useState('all');
  const [tzPref, setTzPref] = useState(() => readStoredTimezone(TZ_STORAGE_KEY));
  const [summaryGrouping, setSummaryGrouping] = useState('total');
  const [memberGrouping, setMemberGrouping] = useState('total');
  const [channelGrouping, setChannelGrouping] = useState('total');

  useEffect(() => { storeTimezone(TZ_STORAGE_KEY, tzPref); }, [tzPref]);
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, reportView);
    } catch {
      // private mode / storage disabled: the choice just isn't remembered
    }
  }, [reportView]);
  const isOverview = reportView === 'overview';

  // 'auto' resolves to the browser zone each render so travel is reflected.
  const tz = useMemo(() => (tzPref === 'auto' ? moment.tz.guess() : tzPref), [tzPref]);
  const tzLabel = useMemo(() => {
    const opt = REPORT_TIMEZONE_OPTIONS.find((o) => o.id === tzPref);
    if (!opt) return 'My Current Timezone';
    return tzPref === 'auto' ? `My Current Timezone (${tz})` : opt.label;
  }, [tzPref, tz]);

  // The picker returns browser-local moments; reinterpret the calendar dates
  // in the chosen zone so "Apr 1" means Apr 1 there. The API buckets in `tz`.
  const pickedStart = useMemo(() => moment.tz(startDate.format('YYYY-MM-DD'), tz).startOf('day'), [startDate, tz]);
  const pickedEnd = useMemo(() => moment.tz(endDate.format('YYYY-MM-DD'), tz).startOf('day'), [endDate, tz]);
  const from = pickedStart.format('YYYY-MM-DD');
  const to = pickedEnd.format('YYYY-MM-DD');
  const days = useMemo(() => daysInRange(pickedStart, pickedEnd), [pickedStart, pickedEnd]);

  const { data: overageStatus } = useGetMessageOverageStatusQuery();
  const { data: breakdown, isLoading, isFetching } = useGetMessageBreakdownQuery({ from, to, tz });
  const { data: contacts } = useGetInboundContactsQuery({ from, to });
  const { data: groupsData, isFetching: groupsFetching } = useGetMessagesByGroupQuery({ from, to }, { skip: activeTab !== 'groups' });
  const { data: remindersData, isFetching: remindersFetching } = useGetRemindersSummaryQuery({ from, to }, { skip: activeTab !== 'reminders' });
  // Current state (not range-bound): conversations assigned to each member right now.
  const { data: openByMemberData } = useGetOpenByMemberQuery(undefined, { skip: activeTab !== 'members' });

  const members = breakdown?.members || [];
  const channels = breakdown?.channels || [];
  const memberName = useCallback((id) => {
    const m = members.find((member) => member.id === id);
    if (!m) return `Member ${id}`;
    if (m.deleted) return `${m.name} (deleted)`;
    return m.inactive ? `${m.name} (deactivated)` : m.name;
  }, [members]);
  const channelName = useCallback((id) => {
    const c = channels.find((ch) => ch.id === id);
    if (!c) return `Channel ${id}`;
    return c.deleted ? `${c.name} (deleted)` : c.name;
  }, [channels]);

  const filters = useMemo(() => ({ memberId: selectedMember, channelId: selectedChannel, kind: selectedKind }), [selectedMember, selectedChannel, selectedKind]);
  const daily = useMemo(() => filterRows(breakdown?.daily || [], filters), [breakdown, filters]);
  const byHour = useMemo(() => filterRows(breakdown?.byHour || [], filters), [breakdown, filters]);
  const byWeekday = useMemo(() => filterRows(breakdown?.byWeekday || [], filters), [breakdown, filters]);

  // --- Summary ---
  const totals = useMemo(() => sumRows(daily), [daily]);
  const summaryRows = useMemo(() => summaryByPeriod(daily, days, summaryGrouping), [daily, days, summaryGrouping]);
  const directionPie = useMemo(() => kindPie(totals), [totals]);
  const channelPie = useMemo(() => pieOf(totalsByCategory(daily, (r) => r.channelId, channelName, days.length)), [daily, channelName, days.length]);
  const channelTypePie = useMemo(() => {
    const typeOf = (id) => channelTypeLabel(channels.find((c) => c.id === id)?.typeId);
    return pieOf(totalsByCategory(daily, (r) => typeOf(r.channelId), (k) => k, days.length));
  }, [daily, channels, days.length]);
  const hourSeries = useMemo(() => perHourSeries(byHour, days.length), [byHour, days.length]);
  const weekdaySeries = useMemo(() => perWeekdaySeries(byWeekday, days), [byWeekday, days]);
  const daySeries = useMemo(() => perDaySeries(daily, days), [daily, days]);
  const peak = useMemo(() => peakInboundHour(byHour), [byHour]);
  const avgResponse = useMemo(() => {
    const withResponse = daySeries.filter((d) => d.avgResponseMinutes !== null);
    return { points: daySeries, avg: summaryByPeriod(daily, days, 'total')[0]?.avgResponseMinutes ?? null, hasAny: withResponse.length > 0 };
  }, [daySeries, daily, days]);
  const contactPie = useMemo(() => {
    if (!contacts?.contacts) return [];
    return Object.entries(contacts.contacts).map(([source, value]) => ({ name: capitalize(source), value })).filter((s) => s.value > 0);
  }, [contacts]);

  // --- Members (outbound only: inbound senders are contacts) ---
  const memberRows = useMemo(() => filterRows(daily, { kind: 'outbound' }), [daily]);
  // Every active member is listed; deactivated/deleted ones only when they had activity (API roster).
  const memberSeed = useMemo(
    () => members.filter((m) => selectedMember === null || m.id === selectedMember).map((m) => ({ key: m.id, name: memberName(m.id) })),
    [members, selectedMember, memberName],
  );
  const memberTotals = useMemo(
    () => totalsByCategory(memberRows, (r) => r.memberId, memberName, days.length, memberSeed),
    [memberRows, memberName, days.length, memberSeed],
  );
  const memberTable = useMemo(
    () => categoryByPeriod(memberRows, (r) => r.memberId, memberName, days, memberGrouping, memberSeed),
    [memberRows, memberName, days, memberGrouping, memberSeed],
  );
  const memberDaily = useMemo(() => dailyByCategory(memberRows, (r) => r.memberId, days), [memberRows, days]);
  const memberResponse = useMemo(() => memberTotals.filter((m) => m.avgResponseMinutes !== null), [memberTotals]);
  const openByMember = useMemo(() => {
    const byId = {};
    (openByMemberData?.members || []).forEach((m) => { byId[m.memberId] = m; });
    return byId;
  }, [openByMemberData]);
  // Open counts are current state, not range-bound, so they don't pass through the API's range roster.
  // Apply the same rule here: a deactivated member is shown only when they had activity in the range
  // (i.e. they are in the breakdown's member roster).
  const rosterIds = useMemo(() => new Set(members.map((m) => m.id)), [members]);
  const memberOpenRows = useMemo(() => (openByMemberData?.members || [])
    .filter((m) => m.open > 0 && (selectedMember === null || m.memberId === selectedMember))
    .filter((m) => !m.inactive || rosterIds.has(m.memberId))
    .map((m) => ({ key: m.memberId, name: m.inactive ? `${m.name} (deactivated)` : m.name, openNow: m.open })), [openByMemberData, selectedMember, rosterIds]);
  const memberTotalsWithOpen = useMemo(() => memberTotals.map((m) => ({ ...m, openNow: openByMember[m.key]?.open || 0 })), [memberTotals, openByMember]);

  // --- Channels ---
  // Every active channel is listed; deleted ones only when they had activity (API roster).
  const channelSeed = useMemo(
    () => channels.filter((c) => selectedChannel === null || c.id === selectedChannel).map((c) => ({ key: c.id, name: channelName(c.id) })),
    [channels, selectedChannel, channelName],
  );
  const channelTotals = useMemo(
    () => totalsByCategory(daily, (r) => r.channelId, channelName, days.length, channelSeed),
    [daily, channelName, days.length, channelSeed],
  );
  const channelTable = useMemo(
    () => categoryByPeriod(daily, (r) => r.channelId, channelName, days, channelGrouping, channelSeed),
    [daily, channelName, days, channelGrouping, channelSeed],
  );
  const channelDaily = useMemo(() => dailyByCategory(daily, (r) => r.channelId, days), [daily, days]);
  const channelResponse = useMemo(() => channelTotals.filter((c) => c.avgResponseMinutes !== null), [channelTotals]);

  // --- Groups / Reminders (date range only; existing endpoints) ---
  // Groups: { groups: [{groupId, name, routed, open, closed}], closedByDay: [{day, groupId, count}] }
  // (an older API shape was a plain array of { groupId, name, conversationCount }).
  const groupData = useMemo(() => {
    const raw = Array.isArray(groupsData) ? { groups: groupsData, closedByDay: [] } : (groupsData || {});
    const rows = (raw.groups || []).map((g) => ({
      key: g.groupId,
      name: `${g.name || `Group ${g.groupId}`}${g.deleted ? ' (deleted)' : ''}`,
      routed: Number(g.routed ?? g.conversationCount) || 0,
      open: Number(g.open) || 0,
      closed: Number(g.closed) || 0,
    })).sort((a, b) => b.routed - a.routed || a.name.localeCompare(b.name));
    const nameOf = (id) => (id === OTHER_KEY ? OTHER_LABEL : (rows.find((r) => r.key === id)?.name || `Group ${id}`));
    const closedTotals = {};
    (raw.closedByDay || []).forEach((r) => { closedTotals[r.groupId] = (closedTotals[r.groupId] || 0) + (Number(r.count) || 0); });
    const ranked = Object.keys(closedTotals).sort((a, b) => closedTotals[b] - closedTotals[a]);
    const keys = ranked.length > MAX_SERIES ? [...ranked.slice(0, MAX_SERIES - 1), OTHER_KEY] : ranked;
    const own = new Set(keys);
    const byDay = {};
    days.forEach((day) => {
      byDay[day] = { date: moment(day, 'YYYY-MM-DD').format('MM/DD'), day };
      keys.forEach((k) => { byDay[day][k] = 0; });
    });
    (raw.closedByDay || []).forEach((r) => {
      const key = String(r.groupId);
      if (byDay[r.day]) byDay[r.day][own.has(key) ? key : OTHER_KEY] += Number(r.count) || 0;
    });
    return {
      rows,
      nameOf,
      closedPerDay: days.map((day) => byDay[day]),
      closedKeys: keys,
      totalOpen: rows.reduce((sum, r) => sum + r.open, 0),
      totalClosed: rows.reduce((sum, r) => sum + r.closed, 0),
    };
  }, [groupsData, days]);
  const reminders = useMemo(() => {
    const total = Number(remindersData?.total) || 0;
    const byResponse = (remindersData?.byResponse || []).map((r) => ({ name: r.label, value: Number(r.count) || 0 })).filter((r) => r.value > 0);
    const dayMap = {};
    days.forEach((day) => { dayMap[day] = { date: moment(day, 'YYYY-MM-DD').format('MM/DD') }; });
    (remindersData?.byDay || []).forEach((d) => {
      const key = String(d.day).slice(0, 10);
      if (!dayMap[key]) return;
      dayMap[key][d.label] = (dayMap[key][d.label] || 0) + (Number(d.count) || 0);
    });
    const labels = Object.keys(REMINDER_COLORS).filter((label) => byResponse.some((r) => r.name === label));
    const perDay = days.map((day) => ({ ...dayMap[day], ...Object.fromEntries(labels.map((l) => [l, dayMap[day][l] || 0])) }));
    const count = (label) => byResponse.find((r) => r.name === label)?.value || 0;
    const rate = (n) => (total ? Math.round((n / total) * 100) : 0);
    const rows = labels.map((label) => ({ response: label, count: count(label), pct: rate(count(label)) }));
    return { total, byResponse, perDay, labels, rows, responded: total - count('No Response'), rate };
  }, [remindersData, days]);

  // --- Filters / dates ---
  const memberLabel = selectedMember === null ? 'All Members' : memberName(selectedMember);
  const channelLabel = selectedChannel === null ? 'All Channels' : channelName(selectedChannel);
  const kindLabel = MESSAGE_KINDS.find((k) => k.id === selectedKind)?.label || 'All Messages';
  const isAnyFilterSelected = selectedMember !== null || selectedChannel !== null || selectedKind !== 'all';
  const clearAllFilters = () => { setSelectedMember(null); setSelectedChannel(null); setSelectedKind('all'); };

  const selectDate = (date) => {
    if (date && date.startDate && date.endDate) {
      const start = moment(date.startDate);
      const end = moment(date.endDate);
      if (end.diff(start, 'months', true) > MAX_RANGE_MONTHS) {
        start.set(end.clone().subtract(MAX_RANGE_MONTHS, 'months').toObject());
      }
      setStartDate(start);
      setEndDate(end);
      setActiveKey(date.activeKey);
    }
  };

  const calculateDateRange = (duration, selectedLabelIndex) => {
    let fromDate = moment().subtract(duration, 'days');
    let toDate = moment();
    if (selectedLabelIndex === 1) toDate = moment().subtract(1, 'days');
    if (duration === 'year') fromDate = moment().subtract(1, 'years').add(1, 'days');
    return { startDate: fromDate, endDate: toDate };
  };

  const csvFilename = (title) => `${title}_${from}-${to}.csv`;

  // Publish what is on screen to the Ask Reggie drawer so its answers follow
  // the date range and filters chosen here (same as the Calls page).
  useEffect(() => {
    const rangeText = days.length === 30 ? 'Last 30 days' : `${pickedStart.format('MMM D')} - ${pickedEnd.format('MMM D, YYYY')}`;
    const contextFilters = { tab: activeTab, view: reportView, timezone: tz };
    if (selectedMember !== null) contextFilters.member = memberLabel;
    if (selectedChannel !== null) contextFilters.channel = channelLabel;
    if (selectedKind !== 'all') contextFilters['message type'] = kindLabel;
    dispatch(setAskReggieContext({
      page: 'Messaging Reports',
      label: `Messaging • ${rangeText}`,
      filters: contextFilters,
      dateRange: { from, to },
    }));
  }, [
    dispatch, days.length, pickedStart, pickedEnd, activeTab, reportView, tz,
    selectedMember, memberLabel, selectedChannel, channelLabel, selectedKind, kindLabel, from, to,
  ]);

  useEffect(() => () => { dispatch(clearAskReggieContext()); }, [dispatch]);

  // Members are credited only with messages they send, so inbound and automated are always 0 for
  // them; the member table omits those columns and adds conversations assigned to them now.
  const categoryColumns = (label, grouping) => {
    const isMember = label === 'Member';
    return [
      { key: 'name', label },
      ...(grouping !== 'total' ? [{ key: 'period', label: 'Period' }] : []),
      { key: 'total', label: 'Total Messages' },
      { key: 'avgPerDay', label: 'Avg. Messages/Day' },
      ...(isMember ? [] : [{ key: 'inbound', label: '# Inbound' }]),
      { key: 'outbound', label: isMember ? '# Sent' : '# Outbound' },
      ...(isMember ? [] : [{ key: 'automated', label: '# Automated' }]),
      { key: 'avgResponseMinutes', label: 'Avg. Response Time', format: (r) => formatMinutes(r.avgResponseMinutes) },
      ...(isMember && grouping === 'total' ? [{ key: 'openNow', label: 'Open Now' }] : []),
    ];
  };
  const summaryColumns = [
    { key: 'period', label: 'Period' },
    { key: 'total', label: 'Total Messages' },
    { key: 'avgPerDay', label: 'Avg. Messages/Day' },
    { key: 'inbound', label: '# Inbound' },
    { key: 'outbound', label: '# Outbound' },
    { key: 'automated', label: '# Automated' },
    { key: 'avgResponseMinutes', label: 'Avg. Response Time', format: (r) => formatMinutes(r.avgResponseMinutes) },
  ];

  const renderFilters = () => (
    <div className="cdr-records__filter-wrapper">
      <div className="cdr-records__filter-label">
        FILTER BY:
        {isAnyFilterSelected && (
          <Button type="link" className="clear-all__button" onClick={clearAllFilters}>Clear All &nbsp;<Icon icon="close" /></Button>
        )}
      </div>
      <div className="row">
        <div>
          <UtilityInlineGrid>
            <DateRangeDropdown
              name="Messaging Reports Date Picker"
              startDate={startDate}
              endDate={endDate}
              selectDate={selectDate}
              minDate={moment().subtract(1, 'year')}
              maxDate={moment()}
              activeKey={activeKey}
              position="center"
              dropdownMenuItems={REPORT_DATE_PICKER_LABELS}
              selectDateRange={calculateDateRange}
              dataCypress="messagingReportsDatePicker"
              isCustomDate
            />
            <AnalyticsFilterDropdown
              name="messagingReportsMember"
              label={memberLabel}
              options={[{ id: 'all', label: 'All Members' }, ...members.map((m) => ({ id: m.id, label: memberName(m.id) }))]}
              activeId={selectedMember === null ? 'all' : selectedMember}
              isFiltered={selectedMember !== null}
              onSelect={(id) => setSelectedMember(id === 'all' || id === selectedMember ? null : id)}
              searchPlaceholder="Search members"
              dataCypress="messagingReportsMember"
            />
            <AnalyticsFilterDropdown
              name="messagingReportsChannel"
              label={channelLabel}
              options={[{ id: 'all', label: 'All Channels' }, ...channels.map((c) => ({ id: c.id, label: `${channelName(c.id)} (${channelTypeLabel(c.typeId)})` }))]}
              activeId={selectedChannel === null ? 'all' : selectedChannel}
              isFiltered={selectedChannel !== null}
              onSelect={(id) => setSelectedChannel(id === 'all' || id === selectedChannel ? null : id)}
              searchPlaceholder="Search channels"
              dataCypress="messagingReportsChannel"
            />
            <AnalyticsFilterDropdown
              name="messagingReportsKind"
              label={kindLabel}
              options={MESSAGE_KINDS.map((k) => ({ id: k.id, label: k.label }))}
              activeId={selectedKind}
              isFiltered={selectedKind !== 'all'}
              onSelect={(id) => setSelectedKind(id)}
              searchPlaceholder="Search message types"
              dataCypress="messagingReportsKind"
            />
            <AnalyticsFilterDropdown
              name="messagingReportsTimezone"
              label={tzLabel}
              options={REPORT_TIMEZONE_OPTIONS.map((opt) => ({ id: opt.id, label: opt.label }))}
              activeId={tzPref}
              isFiltered={tzPref !== 'auto'}
              onSelect={(id) => setTzPref(id)}
              searchPlaceholder="Search time zones"
              dataCypress="messagingReportsTimezone"
            />
          </UtilityInlineGrid>
        </div>
      </div>
    </div>
  );

  const renderTabs = () => (
    <div className="voip-reports__tabs">
      <div className="voip-reports__tabs-left">
        {REPORT_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`voip-reports__tab${activeTab === tab.id ? ' voip-reports__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );

  const renderSummary = () => (
    <div className="voip-reports__charts">
      <ChartCard
        title="Message Summary"
        csv={{ columns: csvColumns(summaryColumns), rows: summaryRows, filename: csvFilename('Message Summary') }}
        actions={<GroupingSelector value={summaryGrouping} onChange={setSummaryGrouping} />}
      >
        <SortableTable columns={summaryColumns} rows={summaryRows} defaultSort="period" defaultAsc rowKey={(r) => r.period} />
      </ChartCard>
    </div>
  );

  const renderCategoryTab = ({ label, tableRows, grouping, setGrouping }) => {
    const columns = categoryColumns(label, grouping);
    return (
      <div className="voip-reports__charts">
        <ChartCard
          title={`${label} Breakdown`}
          csv={{ columns: csvColumns(columns), rows: tableRows, filename: csvFilename(`${label} Breakdown`) }}
          actions={<GroupingSelector value={grouping} onChange={setGrouping} />}
        >
          <SortableTable columns={columns} rows={tableRows} defaultSort={grouping === 'total' ? 'total' : 'name'} defaultAsc={grouping !== 'total'} rowKey={(r) => `${r.key}-${r.period || ''}`} />
        </ChartCard>
      </div>
    );
  };

  const renderGroups = () => {
    const { rows } = groupData;
    const columns = [
      { key: 'name', label: 'Group' },
      { key: 'routed', label: 'Conversations Assigned' },
      { key: 'closed', label: 'Conversations Closed' },
      { key: 'open', label: 'Open Now' },
    ];
    if (groupsFetching) return <PageLoader />;
    return (
      <div className="voip-reports__charts">
        <ChartCard title="Group Breakdown" csv={{ columns: csvColumns(columns), rows, filename: csvFilename('Group Breakdown') }}>
          <SortableTable columns={columns} rows={rows} defaultSort="routed" rowKey={(r) => r.key} />
        </ChartCard>
      </div>
    );
  };

  const renderReminders = () => {
    const columns = [
      { key: 'response', label: 'Response' },
      { key: 'count', label: 'Reminders' },
      { key: 'pct', label: '% of Sent', format: (r) => `${r.pct}%` },
    ];
    if (remindersFetching) return <PageLoader />;
    const title = `Reminder Responses (${reminders.total} sent, ${reminders.rate(reminders.responded)}% responded)`;
    return (
      <div className="voip-reports__charts">
        <ChartCard title={title} csv={{ columns: csvColumns(columns), rows: reminders.rows, filename: csvFilename('Reminder Responses') }}>
          <SortableTable columns={columns} rows={reminders.rows} defaultSort="count" rowKey={(r) => r.response} />
        </ChartCard>
      </div>
    );
  };

  const renderViewToggle = () => (
    <div className="msg-overview__view-toggle" role="group" aria-label="Report view">
      {REPORT_VIEWS.map((view) => (
        <button
          key={view.id}
          type="button"
          className={`msg-overview__view-btn${reportView === view.id ? ' msg-overview__view-btn--active' : ''}`}
          aria-pressed={reportView === view.id}
          onClick={() => setReportView(view.id)}
        >
          {view.label}
        </button>
      ))}
    </div>
  );

  const renderOverview = () => {
    if (activeTab === 'groups') return groupsFetching ? <PageLoader /> : <OverviewGroups groupData={groupData} csvFilename={csvFilename} />;
    if (activeTab === 'reminders') {
      return remindersFetching ? <PageLoader /> : <OverviewReminders reminders={reminders} colors={REMINDER_COLORS} csvFilename={csvFilename} />;
    }
    if (isLoading || isFetching) return <PageLoader />;
    if (activeTab === 'members') {
      return (
        <OverviewMembers
          memberTotals={memberTotals}
          memberResponse={memberResponse}
          memberDaily={memberDaily}
          memberName={memberName}
          memberOpenRows={memberOpenRows}
          dayCount={days.length}
          csvFilename={csvFilename}
        />
      );
    }
    if (activeTab === 'channels') {
      return (
        <OverviewChannels
          channelTotals={channelTotals}
          channelResponse={channelResponse}
          channelDaily={channelDaily}
          channelName={channelName}
          dayCount={days.length}
          csvFilename={csvFilename}
        />
      );
    }
    return (
      <OverviewSummary
        totals={totals}
        dayCount={days.length}
        directionPie={directionPie}
        channelPie={channelPie}
        channelTypePie={channelTypePie}
        contacts={contacts}
        contactPie={contactPie}
        hourSeries={hourSeries}
        weekdaySeries={weekdaySeries}
        daySeries={daySeries}
        peak={peak}
        avgResponseMinutes={avgResponse.avg}
        csvFilename={csvFilename}
      />
    );
  };

  const renderActiveTab = () => {
    if (activeTab === 'groups') return renderGroups();
    if (activeTab === 'reminders') return renderReminders();
    if (isLoading || isFetching) return <PageLoader />;
    if (activeTab === 'members') {
      return renderCategoryTab({
        label: 'Member', tableRows: memberGrouping === 'total' ? memberTotalsWithOpen : memberTable, grouping: memberGrouping, setGrouping: setMemberGrouping,
      });
    }
    if (activeTab === 'channels') {
      return renderCategoryTab({
        label: 'Channel', tableRows: channelTable, grouping: channelGrouping, setGrouping: setChannelGrouping,
      });
    }
    return renderSummary();
  };

  return (
    <div className="app-page__container">
      <div className="app-panels">
        <div className="list-panel__wrapper">
          <div className="list-panel">
            <div className="app-page__header">
              <div className="app-page__header__title cdr-records__title rhinoanalyst-title-row">
                Messaging Reports
                <AskReggieButton />
              </div>
            </div>
            {overageStatus?.hasSubscription && (() => {
              const { used, limit, percentage, isAlert, periodStart, periodEnd, messageLimitType } = overageStatus;
              const isYearly = messageLimitType === 3;
              const periodLabel = (() => {
                const s = moment(periodStart).format('MMM D');
                const e = moment(periodEnd).format('MMM D, YYYY');
                if (isYearly) return `Yearly · ${s} – ${e}`;
                return `Monthly · ${s} – ${e}`;
              })();
              const periodWord = isYearly ? 'year' : 'month';
              const tickCount = 4;
              const ticks = Array.from({ length: tickCount + 1 }, (_, i) => Math.round((limit / tickCount) * i));
              const fillPct = Math.min(percentage, 100);
              const warningText = isYearly
                ? `Your organization has used ${percentage}% of its ${limit.toLocaleString()} annual message allowance (${moment(periodStart).format('MMM D')} – ${moment(periodEnd).format('MMM D, YYYY')}). Messages beyond your limit may incur overage charges.`
                : `Your organization has used ${percentage}% of its ${limit.toLocaleString()} monthly message allowance. Messages beyond your limit may incur overage charges.`;
              return (
                <div className="messaging-overage-status">
                  <div className="messaging-overage-status__header">
                    <span className="messaging-overage-status__title">Message Usage</span>
                    <span className="messaging-overage-status__period">{periodLabel}</span>
                    <span className={`messaging-overage-status__pct${isAlert ? ' messaging-overage-status__pct--alert' : ''}`}>{percentage}%</span>
                  </div>
                  <div className={`messaging-overage-status__count${isAlert ? ' messaging-overage-status__count--alert' : ''}`}>
                    <strong>{used.toLocaleString()}</strong>
                    <span> / {limit.toLocaleString()} messages used this {periodWord}</span>
                  </div>
                  <div className="messaging-overage-status__bar-wrap">
                    <div className="messaging-overage-status__bar-track">
                      <div
                        className={`messaging-overage-status__bar-fill${isAlert ? ' messaging-overage-status__bar-fill--alert' : ''}`}
                        style={{ width: `${fillPct}%` }}
                      />
                    </div>
                    <div className="messaging-overage-status__ticks">
                      {ticks.map((t) => (
                        <span key={t}>{t.toLocaleString()}</span>
                      ))}
                    </div>
                  </div>

                  {isAlert && (
                    <div className="messaging-overage-status__warning">
                      <Icon icon="alert-triangle" className="messaging-overage-status__warning-icon" />
                      <span>
                        {warningText}{' '}
                        <a href="https://www.rhinogram.com/submit-a-ticket" target="_blank" rel="noopener noreferrer">Contact support to increase your limit →</a>
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="box responsive-table cdr-records__grid">
              {renderViewToggle()}
              {renderFilters()}
              {renderTabs()}
              {activeTab === 'members' && (
                <p className="u-text-muted u-text-small">
                  Members are credited for the messages they send. Inbound messages come from contacts, so they are not attributed to a member.
                  Open Now is the number of conversations assigned to each member right now, regardless of the date range.
                </p>
              )}
              {activeTab === 'groups' && (
                <p className="u-text-muted u-text-small">
                  Assigned is the number of times a conversation was assigned to the group in the date range, whether manually or by the channel&apos;s
                  default route. Closed is also counted within the date range. Open is the number of conversations currently assigned to each group.
                  Member, channel and message type filters do not apply to this tab.
                </p>
              )}
              {activeTab === 'reminders' && (
                <p className="u-text-muted u-text-small">Member, channel and message type filters do not apply to this tab.</p>
              )}
              {isOverview ? renderOverview() : renderActiveTab()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagingReports;
